import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Edit as EditIcon,
  CheckCircle,
  Clock,
  Loader2,
  Pencil,
  Trash2,
  ChevronRight,
  AlertTriangle,
  FileText,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { format } from "date-fns";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  submitted: "bg-purple-100 text-purple-800 border-purple-200",
  approved: "bg-green-100 text-green-800 border-green-200",
};

const statusDot = {
  pending: "bg-yellow-400",
  in_progress: "bg-blue-400",
  submitted: "bg-purple-400",
  approved: "bg-green-400",
};

const statusLabels = {
  pending: "Pending",
  in_progress: "In Progress",
  submitted: "Done — Awaiting Approval",
  approved: "Approved",
};

const emptyForm = {
  client_name: "",
  client_id: "",
  content_type: "",
  assigned_editor: "",
  deadline: "",
  file_url: "",
};

export default function EditingPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Status change
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusTask, setStatusTask] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingEditors, setLoadingEditors] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    setLoadingEditors(true);
    try {
      const response = await apiClient.get("/users");
      setTeamMembers(response.data.filter((u) => u.role !== "owner"));
    } catch {
      toast.error("Failed to load team members");
    } finally {
      setLoadingEditors(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await apiClient.get("/editing-tasks");
      setTasks(response.data);
    } catch {
      toast.error("Failed to load editing tasks");
    } finally {
      setLoading(false);
    }
  };

  // ── CREATE ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        deadline: new Date(formData.deadline).toISOString(),
      };
      await apiClient.post("/editing-tasks", payload);
      toast.success("Editing task created successfully");
      setOpen(false);
      setFormData(emptyForm);
      fetchTasks();
    } catch {
      toast.error("Failed to create editing task");
    }
  };

  // ── EDIT ──
  const openEdit = (task) => {
    setEditingTask(task);
    const dl = new Date(task.deadline);
    const localDL = new Date(dl.getTime() - dl.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setEditForm({
      client_name: task.client_name,
      client_id: task.client_id || "",
      content_type: task.content_type,
      assigned_editor: task.assigned_editor,
      deadline: localDL,
      file_url: task.file_url || "",
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_name: editForm.client_name,
        content_type: editForm.content_type,
        assigned_editor: editForm.assigned_editor,
        deadline: new Date(editForm.deadline).toISOString(),
        file_url: editForm.file_url,
      };
      await apiClient.patch(`/editing-tasks/${editingTask.id}`, payload);
      toast.success("Task updated successfully");
      setEditOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch {
      toast.error("Failed to update task");
    }
  };

  // ── DELETE ──
  const openDelete = (task) => {
    setDeletingTask(task);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/editing-tasks/${deletingTask.id}`);
      toast.success("Task deleted");
      setDeleteOpen(false);
      setDeletingTask(null);
      fetchTasks();
    } catch {
      toast.error("Failed to delete task");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── STATUS CHANGE ──
  const openStatusChange = (task, initialStatus) => {
    setStatusTask(task);
    setNewStatus(initialStatus || task.status);
    setStatusOpen(true);
  };

  const confirmStatusChange = async () => {
    setStatusLoading(true);
    try {
      await apiClient.patch(`/editing-tasks/${statusTask.id}`, {
        status: newStatus,
      });
      const messages = {
        submitted: "Task marked as done! Owner notified.",
        in_progress: "Task started!",
        approved: "Task approved!",
        pending: "Task reset to pending.",
      };
      toast.success(messages[newStatus] || "Status updated");
      setStatusOpen(false);
      fetchTasks();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setStatusLoading(false);
    }
  };

  // Shortcut: update status without dialog
  const updateStatus = async (taskId, status) => {
    try {
      await apiClient.patch(`/editing-tasks/${taskId}`, { status });
      const messages = {
        submitted: "Task marked as done! The owner has been notified.",
        in_progress: "Task started!",
        approved: "Task approved!",
      };
      toast.success(messages[status] || "Status updated");
      fetchTasks();
    } catch {
      toast.error("Failed to update task");
    }
  };

  const isEditor = user?.role !== "owner" && user?.role !== "pillar";
  const isManager = user?.role === "owner" || user?.role === "pillar";

  const myPendingTasks = tasks.filter((t) =>
    ["pending", "in_progress"].includes(t.status),
  );
  const myDoneTasks = tasks.filter((t) =>
    ["submitted", "approved"].includes(t.status),
  );

  return (
    <div className="space-y-6" data-testid="editing-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1
            className="text-4xl font-bold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Editing Tasks
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage content editing workflow
          </p>
        </div>
        {isManager && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-task-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Task
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Editing Task</DialogTitle>
                <DialogDescription>Assign editing work</DialogDescription>
              </DialogHeader>
              <TaskForm
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleSubmit}
                teamMembers={teamMembers}
                loadingEditors={loadingEditors}
                submitLabel="Create Task"
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update editing task details</DialogDescription>
          </DialogHeader>
          <TaskForm
            formData={editForm}
            setFormData={setEditForm}
            onSubmit={handleEdit}
            teamMembers={teamMembers}
            loadingEditors={loadingEditors}
            submitLabel="Save Changes"
          />
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Task
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the editing task for{" "}
              <strong>{deletingTask?.client_name}</strong>? This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Status Change Dialog ── */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Update status for <strong>{statusTask?.client_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {["pending", "in_progress", "submitted", "approved"].map((s) => (
              <button
                key={s}
                onClick={() => setNewStatus(s)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                  newStatus === s
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-muted-foreground/40"
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${statusDot[s]}`} />
                <span className="text-sm font-medium">{statusLabels[s]}</span>
                {newStatus === s && (
                  <ChevronRight className="w-4 h-4 ml-auto text-primary" />
                )}
              </button>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setStatusOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmStatusChange} disabled={statusLoading}>
              {statusLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…
                </>
              ) : (
                "Apply Status"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Content ── */}
      {loading ? (
        <p className="text-muted-foreground">Loading tasks...</p>
      ) : (
        <>
          {/* Editor view: active tasks */}
          {isEditor && myPendingTasks.length > 0 && (
            <div className="space-y-3">
              <SectionHeader
                icon={<Loader2 className="w-4 h-4" />}
                label="Active Tasks"
              />
              {myPendingTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  user={user}
                  updateStatus={updateStatus}
                  openEdit={openEdit}
                  openDelete={openDelete}
                  openStatusChange={openStatusChange}
                />
              ))}
            </div>
          )}

          {/* Editor view: completed */}
          {isEditor && myDoneTasks.length > 0 && (
            <div className="space-y-3">
              <SectionHeader
                icon={<CheckCircle className="w-4 h-4" />}
                label="Completed"
              />
              {myDoneTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  user={user}
                  updateStatus={updateStatus}
                  openEdit={openEdit}
                  openDelete={openDelete}
                  openStatusChange={openStatusChange}
                />
              ))}
            </div>
          )}

          {/* Manager flat list */}
          {isManager && (
            <div className="grid gap-4">
              {tasks.length === 0 ? (
                <Card className="p-8 text-center">
                  <EditIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No editing tasks yet.</p>
                </Card>
              ) : (
                tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    user={user}
                    updateStatus={updateStatus}
                    openEdit={openEdit}
                    openDelete={openDelete}
                    openStatusChange={openStatusChange}
                  />
                ))
              )}
            </div>
          )}

          {/* Editor empty state */}
          {isEditor &&
            myPendingTasks.length === 0 &&
            myDoneTasks.length === 0 && (
              <Card className="p-8 text-center">
                <EditIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No editing tasks assigned to you yet.
                </p>
              </Card>
            )}
        </>
      )}
    </div>
  );
}

function SectionHeader({ icon, label }) {
  return (
    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
      {icon}
      {label}
    </h2>
  );
}

function TaskCard({
  task,
  user,
  updateStatus,
  openEdit,
  openDelete,
  openStatusChange,
}) {
  const isEditor = user?.role !== "owner" && user?.role !== "pillar";
  const isManager = user?.role === "owner" || user?.role === "pillar";
  const isOverdue =
    new Date(task.deadline) < new Date() &&
    !["submitted", "approved"].includes(task.status);

  return (
    <Card className="p-0 overflow-hidden hover-lift border border-border">
      <div className={`h-1 w-full ${statusDot[task.status]}`} />
      <div className="p-5">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h3 className="text-lg font-semibold truncate">
                {task.client_name}
              </h3>
              <Badge className={`text-xs ${statusColors[task.status]}`}>
                <span
                  className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statusDot[task.status]}`}
                />
                {statusLabels[task.status] || task.status.replace(/_/g, " ")}
              </Badge>
              {isOverdue && (
                <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <FileText className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium text-foreground">
                  {task.content_type}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                  {format(new Date(task.deadline), "PPp")}
                </span>
              </div>
              {task.file_url && (
                <div>
                  <a
                    href={task.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-xs"
                  >
                    View File ↗
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end max-w-[200px] sm:max-w-none">
            {/* Editor action buttons */}
            {isEditor && task.status === "pending" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus(task.id, "in_progress")}
              >
                <Clock className="w-3 h-3 mr-1" />
                Start
              </Button>
            )}
            {isEditor && task.status === "in_progress" && (
              <Button
                size="sm"
                onClick={() => updateStatus(task.id, "submitted")}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Mark Done
              </Button>
            )}

            {/* Manager approve */}
            {isManager && task.status === "submitted" && (
              <Button
                size="sm"
                onClick={() => updateStatus(task.id, "approved")}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Approve
              </Button>
            )}

            {/* Change status (manager) */}
            {isManager && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openStatusChange(task, task.status)}
                className="text-muted-foreground"
                title="Change Status"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}

            {/* Edit */}
            {isManager && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openEdit(task)}
                className="text-muted-foreground hover:text-foreground"
                title="Edit Task"
              >
                <Pencil className="w-4 h-4" />
              </Button>
            )}

            {/* Delete */}
            {user?.role === "owner" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openDelete(task)}
                className="text-muted-foreground hover:text-destructive"
                title="Delete Task"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function TaskForm({
  formData,
  setFormData,
  onSubmit,
  submitLabel,
  teamMembers = [],
  loadingEditors = false,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor="client_name">Client Name *</Label>
        <Input
          id="client_name"
          value={formData.client_name}
          onChange={(e) =>
            setFormData({ ...formData, client_name: e.target.value })
          }
          required
        />
      </div>
      <div>
        <Label htmlFor="content_type">Content Type *</Label>
        <Input
          id="content_type"
          placeholder="e.g., Reel, Product Video"
          value={formData.content_type}
          onChange={(e) =>
            setFormData({ ...formData, content_type: e.target.value })
          }
          required
        />
      </div>

      {/* ← Replaced plain text input with team member dropdown */}
      <div>
        <Label htmlFor="assigned_editor">Assign Editor *</Label>
        {loadingEditors ? (
          <p className="text-sm text-muted-foreground mt-1">Loading editors…</p>
        ) : teamMembers.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-1">
            No team members found.
          </p>
        ) : (
          <select
            id="assigned_editor"
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            value={formData.assigned_editor}
            onChange={(e) =>
              setFormData({ ...formData, assigned_editor: e.target.value })
            }
            required
          >
            <option value="">— Choose an editor —</option>
            {teamMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.role})
              </option>
            ))}
          </select>
        )}
      </div>

      <div>
        <Label htmlFor="deadline">Deadline *</Label>
        <div className="mt-1">
          <DateTimePicker
            value={formData.deadline}
            onChange={(val) => setFormData({ ...formData, deadline: val })}
            placeholder="Select task deadline"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="file_url">File URL (optional)</Label>
        <Input
          id="file_url"
          value={formData.file_url}
          onChange={(e) =>
            setFormData({ ...formData, file_url: e.target.value })
          }
        />
      </div>
      <Button type="submit" className="w-full">
        {submitLabel}
      </Button>
    </form>
  );
}
